import type { FC } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";

type ImagePanelVariant = "destructive" | "success";

const variantStyles: Record<ImagePanelVariant, { border: string; bg: string; text: string }> = {
    destructive: {
        border: "border-content-destructive/50",
        bg: "bg-surface-destructive-subtle/20",
        text: "text-content-destructive dark:text-content-destructive-subtle",
    },
    success: {
        border: "border-content-success/50",
        bg: "bg-surface-success-subtle/20",
        text: "text-content-success",
    },
};

const ImagePanel: FC<{
    src: string;
    alt: string;
    label: string;
    variant: ImagePanelVariant;
    className?: string;
}> = ({ src, alt, label, variant, className }) => {
    const styles = variantStyles[variant];
    return (
        <div className={cn("flex flex-col items-center gap-2 border p-3", styles.border, styles.bg, className)}>
            <Text className={cn("select-none text-xs font-medium", styles.text)}>{label}</Text>
            <img src={src} alt={alt} className="max-h-96 select-none rounded border border-border-base object-contain" />
        </div>
    );
};

export const ImageDiffView: FC<{ original?: string; updated?: string }> = ({ original, updated }) => {
    const hasOriginal = !!original;
    const hasUpdated = !!updated;

    return (
        <div className="flex items-start justify-center gap-1 p-4">
            {hasOriginal && (
                <ImagePanel
                    src={original}
                    alt="Original version"
                    label={hasUpdated ? "Original" : "Deleted"}
                    variant="destructive"
                    className={hasUpdated ? "rounded-l-lg rounded-r" : "rounded-lg"}
                />
            )}
            {hasUpdated && (
                <ImagePanel
                    src={updated}
                    alt="Updated version"
                    label={hasOriginal ? "Updated" : "Added"}
                    variant="success"
                    className={hasOriginal ? "rounded-l rounded-r-lg" : "rounded-lg"}
                />
            )}
        </div>
    );
};
